import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TFunction } from "i18next";
import RenderFormRowComponent from "./FormGroups/RenderFormRowComponent";
import { DateInputField } from "@/components/shared";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { AlertTriangleIcon } from "lucide-react";

const createDateSchema = (t: TFunction) => {
  return z.date({
    error: (issue) => issue.input === undefined ?
      t("schemas:createtender.contract.daterequired")
      :
      t("schemas:createtender.contract.dateinvalid"),
  }).refine((date) => date instanceof Date && !isNaN(date.getTime()), {
    message: t("schemas:createtender.contract.dateinvalid"),
  });
};

export const createContractDurationSchema = (t: TFunction) => {
  return z.object({
    contract: z.object({
      startDate: createDateSchema(t),
      endDate: createDateSchema(t),
    }).check((ctx) => {
      if (ctx.value.endDate.getTime() < ctx.value.startDate.getTime()) {
        ctx.issues.push({
          code: "custom",
          path: ["endDate"],
          message: t("schemas:createtender.contract.enddateafterstartdate"),
          input: ctx.value.endDate,
        });
      }
    }),
  });
};

interface ContractDurationFormProps {
  form: UseFormReturn<z.infer<ReturnType<typeof createContractDurationSchema>>>;
  hasServices: boolean;
}

/**
 * ContractDurationForm component for editing the contract duration of a tender
 * Utilises newtender locales as these values are based off the new tender form, if newtender changes, these will change with it.
 * @param props - The component props
 * @returns The rendered contract duration form component
 */
function ContractDurationForm({ form, hasServices }: ContractDurationFormProps) {
  const { t } = useTranslation(["translation"]);

  return (
    <Form {...form}>
      <form>
        <Card className="mt-4">
          <CardHeader className="pt-4 pb-2 select-none">
            <CardTitle className="text-lg">{t("edittender.details.contractduration.title")}</CardTitle>
            <CardDescription className="text-sm text-slate-500">{t("edittender.details.contractduration.description")}</CardDescription>
          </CardHeader>
          <CardContent className="p-2">
            {hasServices && (
              <Card className="flex flex-col gap-2 text-sm mx-4 mb-4 p-2 border-yellow-500 shadow-none bg-yellow-50">
                <div className="flex items-center gap-2 font-semibold text-yellow-500">
                  <AlertTriangleIcon className="w-4 h-4" />
                  <p>{t("edittender.details.contractduration.warning.title")}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <p>{t("edittender.details.contractduration.warning.note")}</p>
                  <p>{t("edittender.details.contractduration.warning.donotclose")}</p>
                  <p>{t("edittender.details.contractduration.warning.caution")}</p>
                </div>
              </Card>
            )}

            {/* Start Date */}
            <RenderFormRowComponent label={t("newtender.formgroups.contract.inputs.startdate.label")}>
              <DateInputField
                form={form}
                name="contract.startDate"
                className="w-full"
                allowOlderDates={true}
              />
            </RenderFormRowComponent>

            {/* End Date */}
            <RenderFormRowComponent label={t("newtender.formgroups.contract.inputs.enddate.label")}>
              <DateInputField
                form={form}
                name="contract.endDate"
                className="w-full"
                allowOlderDates={true}
              />
            </RenderFormRowComponent>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}

export default ContractDurationForm;
